(() => {
  'use strict';

  if (window.__SQ_EXPANDED_CLOSE_GUARD_V8__) return;
  window.__SQ_EXPANDED_CLOSE_GUARD_V8__ = true;
  window.__SQ_EXPANDED_CLOSE_GUARD_V7__ = true;

  const MODAL = '[data-lcs-favorite-expanded]';
  const ROOT = '[data-lcs-expanded-controller],[data-lcs-favorites-page]';
  const CLOSE = '[data-lcs-expanded-close],.lcs-favorite-expanded__close,.lcs-favorite-expanded__overlay';
  const OPENERS = [
    '.sq-universal-bag',
    '.sq-universal-card-trigger',
    '[data-lcs-universal-choose]',
    '[data-pick-quick-open]',
    '[data-sq-universal-expanded-open]',
    '[data-lcs-favorite-expand]',
    '[data-sqyml2-expand]',
    '[data-sq-yml2-expand]',
    '[data-lcs-recommend-expand]',
    '[data-sqfd-open]',
    '[data-sq-favorite-expanded-open]'
  ].join(',');
  const CART_OPENERS = [
    '[data-cart-drawer-toggle]',
    '[data-cart-drawer-open]',
    '[aria-controls="CartDrawer"]',
    '[aria-controls="cart-drawer"]',
    '#cart-icon-bubble'
  ].join(',');

  let session = null;
  let closingPointer = false;
  let suppressCartUntil = 0;

  const stop = (event) => {
    if (event?.cancelable) event.preventDefault();
    event?.stopPropagation?.();
    event?.stopImmediatePropagation?.();
  };

  const modalIsOpen = () =>
    [...document.querySelectorAll(MODAL)].some((modal) =>
      modal instanceof HTMLElement &&
      modal.hidden === false &&
      modal.getAttribute('aria-hidden') !== 'true'
    );

  function readScroll() {
    const root = document.scrollingElement || document.documentElement;
    const bodyStyle = getComputedStyle(document.body);
    const inlineTop = Number.parseFloat(document.body.style.top || '');
    const computedTop = Number.parseFloat(bodyStyle.top || '');
    const top = Number.isFinite(inlineTop) ? inlineTop : computedTop;

    if (bodyStyle.position === 'fixed' && Number.isFinite(top) && top <= 0) {
      return {
        x: window.scrollX || root.scrollLeft || 0,
        y: Math.abs(top)
      };
    }

    const cssY = Number.parseFloat(
      getComputedStyle(document.documentElement).getPropertyValue('--lcs-modal-scroll-y') || ''
    );
    if (Number.isFinite(cssY) && cssY >= 0) {
      return { x: window.scrollX || root.scrollLeft || 0, y: cssY };
    }

    return {
      x: window.scrollX || root.scrollLeft || 0,
      y: window.scrollY || root.scrollTop || 0
    };
  }

  function beginSession(opener) {
    if (session) return session;
    const scroll = readScroll();
    const element = opener instanceof HTMLElement ? opener : null;
    const useAnchor = element && !element.closest('#search-modal,search-modal,[data-search-modal]');
    session = {
      x: Math.max(0, Number(scroll.x) || 0),
      y: Math.max(0, Number(scroll.y) || 0),
      opener: element,
      openerTop: useAnchor ? element.getBoundingClientRect().top : null
    };
    window.__SQ_EXPANDED_RETURN_STATE__ = session;
    if (element) window.__SQ_LAST_EXPANDED_OPENER__ = element;
    return session;
  }

  function clearExpandedLock() {
    try { window.LCSExpandedModalLock?.unlock?.(); } catch (_) {}
    const html = document.documentElement;
    const body = document.body;
    html.classList.remove('lcs-expanded-solid-lock');
    body.classList.remove('lcs-expanded-solid-lock');
    html.style.removeProperty('--lcs-modal-scroll-y');

    // Do not tear down a lock that belongs to another still-open dialog
    // (Search, cart, filter drawer, etc.). Those surfaces own their own body lock.
    if (document.querySelector('dialog[open]')) return;

    html.style.overflow = '';
    body.style.overflow = '';
    body.style.position = '';
    body.style.inset = '';
    body.style.top = '';
    body.style.left = '';
    body.style.right = '';
    body.style.width = '';
  }

  function restore(snapshot = session) {
    if (!snapshot) return;

    const restoreY = () => {
      if (modalIsOpen()) return;
      window.scrollTo({ left: snapshot.x, top: snapshot.y, behavior: 'auto' });
    };

    requestAnimationFrame(() => {
      restoreY();
      requestAnimationFrame(() => {
        restoreY();

        const opener = snapshot.opener;
        if (
          opener instanceof HTMLElement &&
          opener.isConnected &&
          Number.isFinite(snapshot.openerTop) &&
          !opener.closest('#search-modal,search-modal,[data-search-modal]')
        ) {
          const delta = opener.getBoundingClientRect().top - snapshot.openerTop;
          if (Math.abs(delta) > 1) window.scrollBy({ left: 0, top: delta, behavior: 'auto' });
        }
      });
    });
  }

  function collapseOpeners() {
    document.querySelectorAll(OPENERS).forEach((button) => {
      if (!(button instanceof HTMLElement)) return;
      button.setAttribute('aria-expanded', 'false');
      button.classList.remove(
        'sq-bag-expanded',
        'sq-bag-pill-expanded',
        'sq-universal-bag-expanded',
        'sq-bag-card-active',
        'is-open',
        'is-active',
        'is-expanded'
      );
    });
  }

  function controllerFor(target = null) {
    const directRoot = target?.closest?.(ROOT);
    const direct = directRoot?.__lcsFavoritesPage || directRoot?.__lcsExpandedController || directRoot?.__sqExpandedController;
    if (direct?.closeExpanded) return direct;

    if (window.SQUniversalExpandedCard?.closeExpanded) return window.SQUniversalExpandedCard;

    for (const root of document.querySelectorAll(ROOT)) {
      const controller = root.__lcsFavoritesPage || root.__lcsExpandedController || root.__sqExpandedController;
      if (controller?.closeExpanded) return controller;
    }
    return null;
  }

  function armCartSuppression(ms = 650) {
    suppressCartUntil = Math.max(suppressCartUntil, Date.now() + ms);
    window.__SQ_SUPPRESS_CART_OPEN_UNTIL__ = Math.max(
      Number(window.__SQ_SUPPRESS_CART_OPEN_UNTIL__ || 0),
      suppressCartUntil
    );
  }

  function patchController(controller) {
    if (!controller || controller.__sqExpandedScrollReturnV8) return Boolean(controller);
    controller.__sqExpandedScrollReturnV8 = true;

    const originalOpen = controller.openExpanded?.bind(controller);
    if (originalOpen) {
      controller.openExpanded = function sqOpenWithStableOrigin(...args) {
        // Never replace a pre-pointer snapshot. This was the regression:
        // later wrappers could recapture after scroll-lock/layout work.
        beginSession(window.__lcsExpandedReturnFocus || window.__SQ_LAST_EXPANDED_OPENER__ || document.activeElement);
        return originalOpen(...args);
      };
    }

    const originalClose = controller.closeExpanded?.bind(controller);
    if (originalClose) {
      controller.closeExpanded = function sqCloseWithStableOrigin(...args) {
        const snapshot = session || window.__SQ_EXPANDED_RETURN_STATE__ || readScroll();
        const result = originalClose(...args);
        collapseOpeners();
        clearExpandedLock();
        restore(snapshot);
        session = null;
        window.__SQ_EXPANDED_RETURN_STATE__ = null;
        return result;
      };
    }
    return true;
  }

  function patchWhenReady() {
    let attempts = 0;
    const wait = () => {
      if (patchController(controllerFor())) return;
      if (attempts++ < 240) requestAnimationFrame(wait);
    };
    wait();
  }

  function closeOnly(target) {
    const snapshot = session || beginSession(window.__SQ_LAST_EXPANDED_OPENER__) || readScroll();
    const controller = controllerFor(target);
    patchController(controller);

    // The patched shared controller is the primary close owner. It performs
    // exactly one close + scroll restoration cycle. Only use the manual path
    // when no controller is available or its close throws.
    if (controller?.closeExpanded) {
      try {
        controller.closeExpanded();
        return;
      } catch (_) {}
    }

    document.querySelectorAll(MODAL).forEach((modal) => {
      if (!(modal instanceof HTMLElement)) return;
      modal.hidden = true;
      modal.setAttribute('aria-hidden', 'true');
      modal.classList.remove('is-ready', 'has-expanded-options');
    });
    document.querySelectorAll(ROOT).forEach((root) => root.classList.remove('lcs-expanded-active'));
    collapseOpeners();
    clearExpandedLock();
    restore(snapshot);

    session = null;
    window.__SQ_EXPANDED_RETURN_STATE__ = null;
  }

  window.addEventListener('pointerdown', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const opener = target?.closest(OPENERS);
    if (opener && !modalIsOpen()) {
      beginSession(opener);
      armCartSuppression();
    }

    const close = target?.closest(CLOSE);
    if (!close?.closest(MODAL)) return;

    stop(event);
    closingPointer = true;
    armCartSuppression();
    closeOnly(close);
  }, { capture: true, passive: false });

  window.addEventListener('pointerup', (event) => {
    if (!closingPointer) return;
    stop(event);
    requestAnimationFrame(() => { closingPointer = false; });
  }, { capture: true, passive: false });

  window.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const opener = target?.closest(OPENERS);
    if (opener && !modalIsOpen()) beginSession(opener);

    if (closingPointer) {
      stop(event);
      closingPointer = false;
      return;
    }

    const close = target?.closest(CLOSE);
    if (close?.closest(MODAL)) {
      stop(event);
      armCartSuppression();
      closeOnly(close);
      return;
    }

    if (Date.now() < suppressCartUntil && target?.closest(CART_OPENERS)) stop(event);
  }, { capture: true, passive: false });

  window.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape' || !modalIsOpen()) return;
    stop(event);
    armCartSuppression();
    closeOnly(document.querySelector(`${MODAL}:not([hidden])`));
  }, true);

  document.readyState === 'loading'
    ? document.addEventListener('DOMContentLoaded', patchWhenReady, { once: true })
    : patchWhenReady();
  document.addEventListener('shopify:section:load', patchWhenReady);
})();
